<template>
  <form-page-template>
    <div class="uc-form">
      <div class="uc-row justify-between mb-16">
        <router-link
          :to="`/${$i18n.locale}/login`"
          class="uc-form-header-link"
          >{{ $t('volunteerPartnerSignUp.login') }}</router-link
        >
        <div class="uc-form-header-link--active">
          {{ $t('volunteerPartnerSignUp.signUp') }}
        </div>
      </div>

      <form
        v-if="formStep === 'step-1'"
        class="uc-form-body"
        @submit.prevent="formStepTwo()"
      >
        <div v-if="errors.length" class="step-errors">
          <h5>{{ $t('volunteerPartnerSignUp.errorTitle') }}</h5>
          <ul>
            <li v-for="error in errors" v-bind:key="error">{{ error }}</li>
          </ul>
        </div>

        <div class="step-header">
          <div class="step-header__title">
            {{ welcomeTitle }}
          </div>
          <div class="step-header__subtitle">
            {{
              $t('volunteerPartnerSignUp.subtitle', {
                volunteerPartnerName: volunteerPartner.name,
              })
            }}
            <a href="https://upchieve.org/volunteer">{{
              $t('volunteerPartnerSignUp.clickHere')
            }}</a>
          </div>
        </div>

        <div class="uc-column">
          <label for="inputEmail" class="uc-form-label">{{
            emailInputLabel
          }}</label>
          <input
            id="inputEmail"
            type="email"
            class="uc-form-input"
            v-bind:class="{
              'uc-form-input--invalid':
                invalidInputs.indexOf('inputEmail') > -1,
            }"
            v-model="formData.email"
            required
            autofocus
            autocomplete="email"
          />
        </div>

        <div class="uc-column">
          <label for="inputEmail" class="uc-form-label">{{
            $t('volunteerPartnerSignUp.passwordLabel')
          }}</label>
          <input
            id="inputPassword"
            type="password"
            class="uc-form-input"
            v-bind:class="{
              'uc-form-input--invalid':
                invalidInputs.indexOf('inputPassword') > -1,
            }"
            v-model="formData.password"
            required
            autocomplete="new-password"
          />
          <p class="uc-form-subtext">
            {{ $t('volunteerPartnerSignUp.passwordSubtext') }}
          </p>
        </div>

        <button
          class="uc-form-button"
          type="submit"
          @click.prevent="formStepTwo()"
        >
          {{ $t('general.continue') }}
        </button>

        <div v-if="serverErrorMsg !== ''">{{ serverErrorMsg }}</div>
      </form>
      <form
        v-if="formStep === 'step-2'"
        class="uc-form-body"
        @submit.prevent="submitSignupForm()"
      >
        <div v-if="errors.length" class="step-errors">
          <h5>{{ $t('volunteerPartnerSignUp.errorTitle') }}</h5>
          <ul>
            <li v-for="error in errors" v-bind:key="error">{{ error }}</li>
          </ul>
        </div>

        <div class="step-header">
          <div class="step-header__title">
            {{ $t('volunteerPartnerSignUp.step-2.title') }}
          </div>
          <div class="step-header__subtitle">
            {{ $t('volunteerPartnerSignUp.step-2.subtitle') }}
          </div>
        </div>

        <div class="uc-column">
          <!-- Fix for bug in Chrome where the first two fields are parsed as a username and password
          even if the HTML5 autocomplete attributes are set to the right values -->
          <label for="username" class="d-none">{{
            $t('volunteerPartnerSignUp.step-2.userNameLabel')
          }}</label>
          <input
            type="text"
            class="d-none"
            id="username"
            v-model="formData.email"
            autocomplete="username"
          />
          <label for="password" class="d-none">{{
            $t('volunteerPartnerSignUp.step-2.passwordLabel')
          }}</label>
          <input
            type="password"
            class="d-none"
            id="password"
            v-model="formData.password"
            autocomplete="new-password"
          />

          <div class="uc-column">
            <label for="firstName" class="uc-form-label">{{
              $t('volunteerPartnerSignUp.step-2.firstNameLabel')
            }}</label>
            <div class="uc-row name-fields">
              <div class="uc-column">
                <input
                  id="firstName"
                  type="text"
                  class="uc-form-input"
                  v-bind:class="{
                    'uc-form-input--invalid':
                      invalidInputs.indexOf('firstName') > -1,
                  }"
                  v-model="formData.firstName"
                  required
                  autofocus
                  autocomplete="given-name"
                />
                <p class="uc-form-subtext">
                  {{ $t('volunteerPartnerSignUp.step-2.firstNameSubtext') }}
                </p>
              </div>
              <div class="uc-column">
                <input
                  id="lastName"
                  type="text"
                  class="uc-form-input"
                  v-bind:class="{
                    'uc-form-input--invalid':
                      invalidInputs.indexOf('lastName') > -1,
                  }"
                  v-model="formData.lastName"
                  required
                  autocomplete="family-name"
                />
                <p class="uc-form-subtext">
                  {{ $t('volunteerPartnerSignUp.step-2.lastNameSubtext') }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="uc-column">
          <label for="phoneNumber" class="uc-form-label">{{
            $t('volunteerPartnerSignUp.step-2.phoneNumberLabel')
          }}</label>
          <vue-phone-number-input
            id="phoneNumber"
            class="phone-input"
            v-model="formData.phone"
            :required="true"
            :error="
              invalidInputs.indexOf('phone') > -1 && !phoneInputInfo.isValid
            "
            color="#555"
            valid-color="#16ba97"
            @update="onPhoneInputUpdate"
          />
          <p class="uc-form-subtext">
            {{ $t('volunteerPartnerSignUp.step-2.phoneSubtext') }}
          </p>
        </div>

        <div class="uc-form-checkbox">
          <input
            id="userAgreement"
            v-model="formData.terms"
            type="checkbox"
            required
          />
          <label
            for="userAgreement"
            v-html="$t('volunteerPartnerSignUp.step-2.userAgreement')"
          />
        </div>

        <button class="uc-form-button" type="submit" :disabled="isRegistering">
          {{ $t('volunteerPartnerSignUp.step-2.submitButton') }}
        </button>
        <loader class="register-loader" v-if="isRegistering" />

        <div v-if="serverErrorMsg !== ''">{{ serverErrorMsg }}</div>
      </form>
      <div v-if="formStep === 'success'" class="uc-form-body">
        {{ $t('volunteerPartnerSignUp.successMessage') }}
      </div>
    </div>
  </form-page-template>
</template>

<script>
import validator from 'validator'
import LoggerService from '@/services/LoggerService'

import FormPageTemplate from '@/components/FormPageTemplate.vue'
import AuthService from '@/services/AuthService'
import NetworkService from '@/services/NetworkService'
import AnalyticsService from '@/services/AnalyticsService'
import VuePhoneNumberInput from 'vue-phone-number-input'
import Loader from '@/components/Loader.vue'
import { EVENTS } from '@/consts'
import i18n from '@/i18n'

export default {
  name: 'volunteer-partner-signup-view',
  components: {
    FormPageTemplate,
    VuePhoneNumberInput,
    Loader,
  },
  beforeRouteEnter(to, from, next) {
    const partnerId = to.params.partnerId

    NetworkService.getVolunteerPartner(partnerId)
      .then(res => {
        const volunteerPartner = res.data.volunteerPartner
        if (!volunteerPartner) return next('/sign-up')
        if (volunteerPartner.deactivated) {
          AnalyticsService.captureEvent(
            EVENTS.VOLUNTEER_VISITED_DEACTIVATED_PARTNER,
            { partner: partnerId }
          )
          return next('/sign-up')
        }
        return next(_this => _this.setVolunteerPartner(volunteerPartner))
      })
      .catch(err => {
        if (err?.response?.status !== 404) {
          // we shouldn't get 422 here, since semantics of GET request are expected
          // to be correct regardless of user input
          LoggerService.noticeError(err)
        }
        return next('/sign-up')
      })
  },
  created() {
    this.$store.dispatch('app/hideNavigation')
  },
  data() {
    return {
      volunteerPartner: {
        name: '',
        requiredEmailDomains: [],
      },
      formStep: 'step-1',
      formData: {
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        phone: '',
        terms: false,
      },
      phoneInputInfo: {},
      errors: [],
      invalidInputs: [],
      serverErrorMsg: '',
      isRegistering: false,
    }
  },
  computed: {
    hasEmailValidation() {
      const requiredDomains = this.volunteerPartner.requiredEmailDomains
      return requiredDomains && requiredDomains.length
    },

    welcomeTitle() {
      return this.hasEmailValidation
        ? this.$root.$t('volunteerPartnerSignUp.employeeWelcomeTitle', {
            volunteerPartnerName: this.volunteerPartner.name,
          })
        : this.$root.$t('volunteerPartnerSignUp.volunteerWelcomeTitle', {
            volunteerPartnerName: this.volunteerPartner.name,
          })
    },

    emailInputLabel() {
      return this.hasEmailValidation
        ? this.$root.$t('volunteerPartnerSignUp.emailInputLabel')
        : this.$root.$t('volunteerPartnerSignUp.emailInputLabel-2')
    },
  },
  methods: {
    setVolunteerPartner(volunteerPartner) {
      this.volunteerPartner = volunteerPartner
    },

    isValidPartnerEmail(email) {
      const requiredDomains = this.volunteerPartner.requiredEmailDomains
      if (!(requiredDomains && requiredDomains.length)) return true

      const domain = email.split('@')[1]
      return domain && requiredDomains.indexOf(domain) >= 0
    },

    onPhoneInputUpdate(phoneInputInfo) {
      this.phoneInputInfo = phoneInputInfo
    },

    formStepTwo() {
      // validate input
      this.errors = []
      this.invalidInputs = []
      this.serverErrorMsg = ''

      if (!this.formData.email) {
        this.errors.push(
          this.$root.$t('volunteerPartnerSignUp.errors.emailRequired')
        )
        this.invalidInputs.push('inputEmail')
      } else if (!validator.isEmail(this.formData.email)) {
        // this is necessary because browsers ignore <input type="email"> until the
        // user actually tries to submit the form, which does not occur until step 2
        this.errors.push(
          this.$root.$t('volunteerPartnerSignUp.errors.invalidEmail', {
            formDataEmail: this.formData.email,
          })
        )

        this.invalidInputs.push('inputEmail')
      } else if (!this.isValidPartnerEmail(this.formData.email)) {
        if (this.volunteerPartner.requiredEmailDomains.length < 5) {
          this.errors.push(
            `This page currently only accepts the following domains: ${this.volunteerPartner.requiredEmailDomains.join(
              ', '
            )}. If you work at ${
              this.volunteerPartner.name
            } but don’t have this email domain, please contact our team at support@upchieve.org!`
          )
        } else {
          this.errors.push(
            this.$root.$t('volunteerPartnerSignUp.errors.mustUseCompanyEmail')
          )
        }
        this.invalidInputs.push('inputEmail')
      }

      if (!this.formData.password) {
        this.errors.push(
          this.$root.$t('volunteerPartnerSignUp.errors.passwordRequired')
        )
        this.invalidInputs.push('inputPassword')
      }
      if (this.errors.length) {
        return
      }

      // Check credentials
      AuthService.checkRegister({
        email: this.formData.email,
        password: this.formData.password,
      })
        .then(() => {
          this.formStep = 'step-2'
          this.serverErrorMsg = ''
        })
        .catch(err => {
          this.serverErrorMsg =
            err?.response?.data?.err ??
            this.$root.$t('volunteerPartnerSignUp.errors.failed')
          if (err?.response?.status !== 409 && err?.response?.status !== 422) {
            LoggerService.noticeError(err)
          }
        })
    },

    submitSignupForm() {
      this.errors = []
      this.invalidInputs = []

      // validate input
      if (!this.formData.firstName || !this.formData.lastName) {
        this.errors.push(
          this.$root.$t('volunteerPartnerSignUp.errors.mustEnterFullName')
        )
      }
      if (!this.formData.firstName) {
        this.invalidInputs.push('firstName')
      }
      if (!this.formData.lastName) {
        this.invalidInputs.push('lastName')
      }
      if (!this.formData.phone) {
        this.errors.push(
          this.$root.$t('volunteerPartnerSignUp.errors.mustEnterPhone')
        )
        this.invalidInputs.push('phone')
      } else if (!this.phoneInputInfo.isValid || !this.phoneInputInfo.e164) {
        this.errors.push(
          this.$root.$t('volunteerPartnerSignUp.errors.invalidPhone', {
            formDataPhone: this.formData.phone,
          })
        )
        this.invalidInputs.push('phone')
      }

      if (!this.formData.terms) {
        this.errors.push(
          this.$root.$t('volunteerPartnerSignUp.errors.mustAcceptAgreement')
        )
      }

      if (!this.errors.length) {
        this.register()
      }
    },

    register() {
      this.serverErrorMsg = ''
      if (this.isRegistering) return
      this.isRegistering = true
      AuthService.registerPartnerVolunteer({
        volunteerPartnerOrg: this.$route.params.partnerId,
        partnerUserId: this.$route.query.uid,
        email: this.formData.email,
        password: this.formData.password,
        firstName: this.formData.firstName,
        lastName: this.formData.lastName,
        phone: this.phoneInputInfo.e164,
        terms: this.formData.terms,
      })
        .then(() => {
          this.isRegistering = false
          this.$router.push(`/${i18n.locale}/verify`)
        })
        .catch(err => {
          this.isRegistering = false
          this.serverErrorMsg =
            err?.response?.data?.err ??
            this.$root.$t('volunteerPartnerSignUp.errors.failed')
          if (err?.response?.status !== 409 && err?.response?.status !== 422) {
            LoggerService.noticeError(err)
          }
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.uc-form-body {
  @include child-spacing(top, 25px);
  position: relative;
}

.step-header {
  text-align: left;

  &__title {
    font-size: 18px;
    font-weight: bold;
  }

  &__subtitle {
    font-size: 14px;
    color: $c-secondary-grey;
  }

  a {
    color: $c-information-blue;
  }
}

.name-fields {
  @include child-spacing(right, 15px);

  input {
    box-sizing: border-box;
    width: 100%;
    height: 45px;
  }
}

.phone-input {
  margin: 10px 0 2px;
}

.step-errors {
  color: #bf0000;
  font-size: 14px;
  text-align: left;
}

.d-none {
  display: none !important;
}

.register-loader {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
}

.mb-16 {
  margin-bottom: 40px;
}

html.is-rtl {
  .step-header {
    text-align: right;
  }

  .name-fields {
    @include child-spacing-rtl(right, 15px);
  }

  .step-errors {
    text-align: right;
  }

  .register-loader {
    right: 50%;
    left: unset;
  }
}
</style>
